import { C, F, CTA_PRIMARY, SiteHeader, SiteFooter } from '../components/SiteChrome';
import { MimiqueIcon } from '../components/MimiqueLogo';
import { useInstallPrompt } from '../lib/installPrompt';
import { usePageSeo } from '../lib/seo';

/**
 * A public page for putting Mimique on a phone's home screen.
 *
 * The same thing exists in Settings, but that sits behind a login, and an
 * email or a social post needs an address anyone can open. There is no such
 * thing as a link that installs an app: on Android the browser has to offer
 * it, and on iOS the reader does it themselves through the Share sheet. So
 * this page does what a link can do, which is put the button in front of
 * people who can press it and give everyone else the steps.
 */

const Step = ({ n, children }) => (
    <li style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 14 }}>
        <span style={{ width: 26, height: 26, borderRadius: 999, background: C.faqBg, border: `1px solid ${C.faqBorder}`, color: C.primary, fontWeight: 800, fontSize: 13.5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {n}
        </span>
        <span style={{ fontSize: 15.5, lineHeight: 1.6, color: C.bodyOnWhite2 }}>{children}</span>
    </li>
);

const Install = () => {
    const { canInstall, needsIosInstructions, installed, promptInstall } = useInstallPrompt();

    usePageSeo({
        title: 'Add to home screen | Mimique',
        description: 'Put Mimique on your phone in a couple of taps. The full app, straight into your practice. No app store, nothing to download.',
        canonical: 'https://mimique.app/install',
    });

    return (
        <div style={{ minHeight: '100vh', background: '#ffffff', color: C.deep, fontFamily: F.body }}>
            <SiteHeader />

            <div style={{ maxWidth: 640, margin: '0 auto', padding: '64px 24px 90px', textAlign: 'center' }}>
                <div style={{ display: 'inline-flex', padding: 14, borderRadius: 28, background: C.faqBg, border: `1px solid ${C.faqBorder}`, marginBottom: 26 }}>
                    <MimiqueIcon size={72} variant="bare" onLight />
                </div>

                <h1 style={{ margin: '0 0 14px', fontFamily: F.heading, fontSize: 36, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15, color: C.deep }}>
                    Put Mimique on your home screen
                </h1>
                <p style={{ margin: '0 0 34px', fontSize: 16.5, lineHeight: 1.7, color: C.bodyOnWhite }}>
                    The full app, on your home screen with your other apps, opening straight into your
                    practice. No app store, nothing to download, and it takes two taps.
                </p>

                {installed && (
                    <div style={{ padding: '20px 24px', borderRadius: 20, background: C.faqBg, border: `1px solid ${C.faqBorder}`, fontSize: 15.5, color: C.deep }}>
                        Mimique is already on this device's home screen. Close this and open it from there.
                    </div>
                )}

                {/* Android and desktop Chrome or Edge, once the browser has
                    decided the app qualifies: the real install dialog. */}
                {!installed && canInstall && (
                    <>
                        <button className="mq-cta mq-arrowbtn" onClick={promptInstall} style={{ ...CTA_PRIMARY, padding: '15px 38px' }}>
                            Add to home screen<span className="mq-arrow" aria-hidden="true">&rarr;</span>
                        </button>
                        <p style={{ margin: '18px 0 0', fontSize: 14, color: C.light }}>
                            Your browser will ask you to confirm.
                        </p>
                    </>
                )}

                {/* iPhone and iPad. Apple offers no install API, so the Share
                    sheet is the only route and saying so is all we can do. */}
                {!installed && !canInstall && needsIosInstructions && (
                    <ol style={{ margin: 0, padding: 0, listStyle: 'none', textAlign: 'left', maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>
                        <Step n="1">Tap the <strong>Share</strong> button, the square with an arrow coming out of it. Can't see it at the bottom of Safari? Tap <strong>···</strong> first, Share is in there.</Step>
                        <Step n="2">Scroll down and tap <strong>Add to Home Screen</strong>.</Step>
                        <Step n="3">Tap <strong>Add</strong>. The Mimique icon appears with your other apps.</Step>
                    </ol>
                )}

                {/* A computer, or a browser that has not offered it yet. */}
                {!installed && !canInstall && !needsIosInstructions && (
                    <div style={{ textAlign: 'left', maxWidth: 460, margin: '0 auto' }}>
                        <p style={{ margin: '0 0 18px', fontSize: 15.5, lineHeight: 1.7, color: C.bodyOnWhite2 }}>
                            <strong>On your phone:</strong> open <span style={{ color: C.primary, fontWeight: 600 }}>mimique.app/install</span> in
                            your browser and this page will show you the button. On an iPhone, tap Share (inside
                            the ··· button on newer iPhones), then Add to Home Screen.
                        </p>
                        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.7, color: C.bodyOnWhite2 }}>
                            <strong>On a computer:</strong> look for the install icon at the right-hand end of
                            the address bar in Chrome or Edge.
                        </p>
                    </div>
                )}

                <p style={{ margin: '38px 0 0', fontSize: 14.5, lineHeight: 1.7, color: C.light }}>
                    Not signed up yet? <a href="/" style={{ color: C.primary, fontWeight: 600 }}>Start with Mimique free</a>.
                </p>
            </div>

            <SiteFooter />
        </div>
    );
};

export default Install;
